"use client";

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import AssistantOnboardingHeader from "../../components/AssistantOnboardingHeader";
import providersService from "../../api/services/providersService";
import useEditFlow from "../../hooks/useEditFlow";
import { getStoredUserIdentifier } from "../../utils/authSession";
import { useToast } from "../../hooks/useToast";
import { validateUploadFile } from "../../utils/directUpload";

const uploadCards = [
  {
    key: "license",
    title: "Upload Tour Guide Licence",
    helper: "To ensure optimal visuals, please upload high-quality images.",
    required: false,
  },
  {
    key: "id",
    title: "Upload Drivers License",
    helper: "Passport, Driving Licence or NIN. Must show full name and photo clearly.",
    required: true,
  },
];

// ✅ same idea as expertise: unwrap nested response shapes
const unwrapDocuments = (res) => {
  if (!res) return {};
  return (
    res?.data?.data?.profile ||
    res?.data?.data?.documents ||
    res?.data?.data ||
    res?.data?.profile ||
    res?.data?.documents ||
    res?.data ||
    res
  );
};

const getFilenameFromUrl = (value = "") => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.split("?")[0];
  const parts = trimmed.split("/");
  return parts[parts.length - 1] || "";
};

const resolveExistingName = (source) => {
  if (!source) return "";
  if (typeof source === "string") return getFilenameFromUrl(source) || source;
  return source.name || getFilenameFromUrl(source.url || source.file?.url || "");
};

const isDocumentValue = (value) => {
  if (!value) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (/^[a-f0-9]{24}$/i.test(trimmed)) return false;
    return (
      /^https?:\/\//i.test(trimmed) ||
      trimmed.includes("/") ||
      /\.[a-z0-9]{2,4}$/i.test(trimmed)
    );
  }
  if (typeof value === "object") {
    return Boolean(
      value.url ||
        value.file?.url ||
        value.fileUrl ||
        value.path ||
        value.filename
    );
  }
  return false;
};

const pickDocumentSource = (candidates) => {
  for (const candidate of candidates) {
    if (isDocumentValue(candidate)) return candidate;
  }
  return "";
};

export default function DocumentVerification({ currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();

  const fromProfile = Boolean(location.state?.fromProfile);
  const isEditingProfile = useEditFlow(fromProfile);
  const toast = useToast();

  const userIdentifier = currentUser?.id || currentUser?.email || getStoredUserIdentifier() || "guest";
  const storageKey = `assistantDocumentDraft_${userIdentifier}`;

  const fileInputs = {
    license: useRef(null),
    id: useRef(null),
  };

  const [files, setFiles] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const [fileMeta, setFileMeta] = useState({ license: "", id: "" });
  const [isHydrated, setIsHydrated] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  const [existingFiles, setExistingFiles] = useState({ license: "", id: "" });
  const [hasLoadedExisting, setHasLoadedExisting] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);

  const handleFileChange = (key, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateUploadFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setFiles((prev) => ({ ...prev, [key]: file }));
    setFileMeta((prev) => ({ ...prev, [key]: file.name || "" }));
  };

  const handleUploadClick = (key) => fileInputs[key]?.current?.click();

  // 1) Draft hydration (ONLY when NOT editing)
  useEffect(() => {
    if (isEditingProfile) {
      setIsHydrated(true);
      setHasDraft(false);
      return;
    }

    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      setFiles({});
      setFileMeta({ license: "", id: "" });
      setHasDraft(false);
      setIsHydrated(true);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      setFileMeta({
        license: parsed.license || "",
        id: parsed.id || "",
      });
      setHasDraft(Boolean(parsed.license || parsed.id));
    } catch (e) {
      setHasDraft(false);
    } finally {
      setIsHydrated(true);
    }
  }, [isEditingProfile, storageKey]);

  // 2) Fetch existing docs (edit mode ALWAYS, normal mode only if no draft)
  useEffect(() => {
    if (!isHydrated) return;
    if (!isEditingProfile && hasDraft) return;
    if (hasLoadedExisting) return;

    let active = true;

    const fetchExisting = async () => {
      setIsLoadingExisting(true);

      try {
        const response = await providersService.getDocuments();

        // DEBUG logs (same style as Experience)
        console.log("fromProfile:", fromProfile, "isEditingProfile:", isEditingProfile);
        console.log("documents response:", response);

        const unwrapped = unwrapDocuments(response);
        console.log("documents unwrapped:", unwrapped);

        // IMPORTANT: sometimes unwrapDocuments already returns profile,
        // but if it returns { profile: {...} } we still need to dive in.
        const docRoot = unwrapped?.profile ?? unwrapped;
        console.log("documents final docRoot:", docRoot);

        // Try a wide range of possible keys
        const licenseSource = pickDocumentSource([
          docRoot.license,
          docRoot.licenseDocument,
          docRoot.license_file,
          docRoot.licenseUrl,
          docRoot.documents?.license,
          docRoot.documents?.licenseDocument,
          docRoot.documents?.licenseUrl,
          docRoot.profile?.license,
          docRoot.profile?.documents?.license,
        ]);

        const idSource = pickDocumentSource([
          docRoot.validId,
          docRoot.identification,
          docRoot.idDocument,
          docRoot.idUrl,
          docRoot.documents?.id,
          docRoot.documents?.validId,
          docRoot.documents?.identification,
          docRoot.documents?.idDocument,
          docRoot.documents?.idUrl,
          docRoot.profile?.documents?.id,
        ]);

        if (!active) return;

        setExistingFiles({
          license: resolveExistingName(licenseSource),
          id: resolveExistingName(idSource),
        });
      } catch (e) {
        // ignore fetch errors
      } finally {
        if (active) {
          setHasLoadedExisting(true);
          setIsLoadingExisting(false);
        }
      }
    };

    fetchExisting();
    return () => {
      active = false;
    };
  }, [isHydrated, isEditingProfile, hasDraft, hasLoadedExisting, fromProfile]);

  // 3) Save draft (ONLY when NOT editing)
  useEffect(() => {
    if (!isHydrated || isEditingProfile) return;
    const draft = { license: fileMeta.license, id: fileMeta.id };
    localStorage.setItem(storageKey, JSON.stringify(draft));
  }, [fileMeta, isHydrated, storageKey, isEditingProfile]);

  const isFormComplete = uploadCards.every(
    (card) =>
      !card.required ||
      Boolean(files[card.key]) ||
      Boolean(existingFiles[card.key])
  );

  const handleVerify = async () => {
    const missing = uploadCards
      .filter((c) => c.required && !files[c.key] && !existingFiles[c.key])
      .map((c) => c.key);

    if (missing.length) {
      // Name the document in the message — the toast is detached from the card
      // it refers to, so "this document" would leave the user guessing.
      toast.errors(
        missing.map((key) => {
          const title = uploadCards.find((c) => c.key === key)?.title || "document";
          return fileMeta[key]
            ? `Please reselect the file for "${title}".`
            : `Please complete "${title}".`;
        })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      if (files.license) {
        const onProgress = (percent) =>
          setUploadProgress((prev) => ({ ...prev, license: percent }));

        if (isEditingProfile) {
          await providersService.updateLicense(files.license, { onProgress });
        } else {
          await providersService.uploadLicense(files.license, { onProgress });
        }
      }

      if (files.id) {
        await providersService.uploadIdDocument(files.id, {
          onProgress: (percent) =>
            setUploadProgress((prev) => ({ ...prev, id: percent })),
        });
      }

      localStorage.removeItem(storageKey);
      const navOptions = isEditingProfile ? { state: { fromProfile: true } } : undefined;
      navigate("/assistant/vehicle", navOptions);
    } catch (err) {
      toast.apiError(err, "Unable to upload your documents. Please try again.");
    } finally {
      setIsSubmitting(false);
      setUploadProgress({});
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <AssistantOnboardingHeader />

      <main className="mx-auto flex max-w-4xl flex-col px-4 pb-16 pt-10">
        <div className="text-center space-y-6">
          <h1 className="text-3xl font-semibold text-gray-900">Document Verification</h1>
          <div className="mx-auto h-2 w-full max-w-2xl overflow-hidden rounded-full bg-[#d3d6dd]">
            <div className="h-full w-[60%] rounded-full bg-[#7a0051]" />
          </div>
          <p className="text-md font-medium text-gray-600">
            Please provide the information requested below.
          </p>
        </div>

        <div className="mt-10 space-y-6">
          {uploadCards.map((card) => (
            <div
              key={card.key}
              className="rounded-xl border-2 border-dashed border-[#cbd0d6] p-6 text-center"
            >
              <p className="text-xl font-semibold text-gray-900">
                {card.title}
                {card.required ? <span className="font-bold text-red-600"> *</span> : null}
              </p>
              <p className="mt-1 text-md text-gray-600">{card.helper}</p>

              <div className="mt-4">
                <input
                  ref={fileInputs[card.key]}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(card.key, e)}
                />
                <button
                  type="button"
                  onClick={() => handleUploadClick(card.key)}
                  className="inline-flex max-w-full items-center justify-center rounded-lg bg-[#d4b4c5] px-6 py-2 text-sm font-semibold text-white transition hover:bg-[#c299b2] focus:outline-none focus:ring-2 focus:ring-[#7a0051]/50 focus:ring-offset-2"
                >
                  <span className="min-w-0 break-all">
                    {files[card.key]?.name ||
                      (fileMeta[card.key]
                        ? `${fileMeta[card.key]} (reselect)`
                        : existingFiles[card.key]
                          ? `${existingFiles[card.key]} (on file)`
                          : "Browse File")}
                  </span>
                </button>
              </div>

              {typeof uploadProgress[card.key] === "number" && isSubmitting && (
                <div className="mt-3">
                  <div className="mx-auto h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-[#e6e8ec]">
                    <div
                      className="h-full rounded-full bg-[#7a0051] transition-all"
                      style={{ width: `${uploadProgress[card.key]}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Uploading… {uploadProgress[card.key]}%
                  </p>
                </div>
              )}

              {existingFiles[card.key] && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs font-semibold text-emerald-600">
                  <Check size={14} className="shrink-0" />
                  <span>Already uploaded</span>
                  <span className="min-w-0 max-w-full break-all text-slate-600">{existingFiles[card.key]}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-3">
          <button
            type="button"
            onClick={handleVerify}
            className={`w-full rounded-lg px-4 py-3 text-center text-base font-semibold text-white shadow-sm transition ${
              isFormComplete
                ? "bg-[#61013B] hover:bg-[#4f0130]"
                : "bg-[#caa8b8] hover:bg-[#b792a3]"
            }`}
            disabled={!isFormComplete || isSubmitting || isLoadingExisting}
          >
            {isSubmitting ? "Uploading..." : "Verify Documents"}
          </button>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-full rounded-lg border border-[#7a0051] px-4 py-3 text-center text-base font-semibold text-[#7a0051] transition hover:bg-[#f7eef4]"
          >
            Back
          </button>
        </div>
      </main>
    </div>
  );
}
